import { useEffect, useRef, useState } from "react";

const useCarousel = (length: number, autoPlayInterval: number = 0) => {
    const [index, setIndex] = useState<number>(length);
    const [transition, setTransition] = useState<boolean>(true);
    const touchStartX = useRef<number>(0);
    const touchEndX = useRef<number>(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isResetting = useRef<boolean>(false);

    const goTo = (i: number, withTransition = true) => {
        setTransition(withTransition);
        setIndex(i);
    };

    const prev = (): void => {
        if (isResetting.current) return;
        setIndex((prev) => prev - 1);
        setTransition(true);
    };

    const next = (): void => {
        if (isResetting.current) return;
        setIndex((prev) => prev + 1);
        setTransition(true);
    };

    useEffect(() => {
        if (index >= length * 2) {
            isResetting.current = true;
            setTimeout(() => {
                setTransition(false);
                setIndex(length);
                setTimeout(() => {
                    isResetting.current = false;
                }, 50);
            }, 500);
        }

        if (index < length) {
            isResetting.current = true;
            setTimeout(() => {
                setTransition(false);
                setIndex(length * 2 - 1);
                setTimeout(() => {
                    isResetting.current = false;
                }, 50);
            }, 500);
        }
    }, [index, length]);

    const stopAutoPlay = (): void => {
        if (intervalRef.current) clearInterval(intervalRef.current);
    };

    const startAutoPlay = (): void => {
        if (!autoPlayInterval) return;
        stopAutoPlay();
        intervalRef.current = setInterval(next, autoPlayInterval);
    };

    useEffect(() => {
        startAutoPlay();
        return () => stopAutoPlay();
    }, [length, autoPlayInterval]);

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>): void => {
        stopAutoPlay();
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>): void => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (): void => {
        const swipeDistance = touchStartX.current - touchEndX.current;
        const minSwipeDistance = 50;

        if (swipeDistance > minSwipeDistance) next();
        else if (swipeDistance < -minSwipeDistance) prev();

        touchStartX.current = 0;
        touchEndX.current = 0;
        startAutoPlay();
    };

    return {
        index,
        transition,
        goTo,
        prev,
        next,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        startAutoPlay,
        stopAutoPlay,
    };
};

export default useCarousel;